import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectCoordinateSystem,
    selectEnabled,
    selectFilename,
    selectJobId,
    selectNotation,
    selectOrientation,
    selectPageSize,
    setSnapshotOpen,
    selectGridData,
    selectData,
    setData,
    leftLogoDefault,
    selectLegendPosition,
    selectPDFLegendSize,
    selectHideLegend,
} from "./PDFSlice"
import { DEFAULT_HEIGHT, DEFAULT_WIDTH, PDFSnapshotContainer } from "./PDFSnapshotContainer"
import { isCrossOriginLogoSrc, resolveDefaultPdfLogoSrc } from "plugins/shared/pdfLogos"
import { formatShortDate } from "@nova/format"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { useEffect, useMemo, useRef } from "react"
import { MapSnapshot } from "@nova/component-map-snapshot"
import { fetchAsDataUrl, fileManagementApi } from "features/data-management/files/fileManagementApi"
import { toBlob } from "html-to-image"
import {
    selectAllVisibleInfraredFlightsMap,
    selectHotspotOrthoVisibilityMap,
    selectShowLayerByIdMapForFlightPaths,
    selectShowLayerByIdMapForHotspotLabels,
    selectShowLayerByIdMapForHotspotPoints,
    selectShowLayerByIdMapForHotspotPolygons,
    selectShowLayerByIdMapForHotspotScanArea,
    selectShowLayerByIdMapForOrthoScanArea,
    selectVisibleOrthoFlights,
    selectVisibleSatelliteFlights,
} from "features/entity-caching/flights/selectors"
import { cogOrthoBbox, selectActiveCogOrtho } from "plugins/Satellite/layers/satelliteLayer"
import { getSatelliteFlightTileUrl } from "plugins/Satellite/satelliteTileUrl"
import {
    selectOrthoBoundingBoxForFlightId,
    selectOrthoOpacityById,
    selectVisibleActiveOrthophotos,
} from "plugins/Orthomosaic/OrthomosaicSlice"
import { WebMercatorViewport } from "@deck.gl/core"
import { useAsyncCallback } from "@nova/use-async-callback"
import { coordinateSystems, getDecimals } from "./PDFAnnotations/PDFCoordinateNotations"
import canvasSize from "canvas-size"
import { GeometryType, HotspotPointTypes, PDFPanelWidth } from "@nova/constants"
import store from "integrations/redux/store"
import { useMapControl } from "features/map/control/useMapControl"
import { selectActivatedFireId, useMapStylesJSON } from "features/map/control/selectors"
import { getHeatGeoJsonForRegion } from "features/map/layers/overlays/getHeatGeoJsonForRegion"
import { getWildfirePerimetersGeoJsonForRegion } from "features/map/layers/overlays/getWildfirePerimetersGeoJsonForRegion"
import { useOverlays } from "features/map/control/useOverlays"
import { GIBS_ASTER_GDEM } from "features/map/layers/overlays/useElevationLayer"
import { GIBS_IMPERVIOUS } from "features/map/layers/overlays/useCivilizationLayer"
import { FIRE_DANGER_WMS_SOURCES } from "features/map/layers/overlays/useFireDangerLayer"
import { GEOSTATIONARY_WMS_SOURCE } from "features/map/layers/overlays/useGeostationaryLayer"
import {
    selectBoundingBoxForMergedFlight,
    selectHotspotBoundingBoxForFlightId,
} from "plugins/InfraredImage/InfraredImageSlice"
import { calculateBoundingBoxCornerPoints } from "@nova/calculate-geospatial"
import { selectVisibleTexts } from "features/entity-caching/texts/selectors"
import { createPDFJob } from "features/pubsub/entityActions/jobs/pdf"
import { selectVisibleLines } from "features/entity-caching/lines/selectors"
import { useCursorTracking } from "tools/events/useCursorProvider"
import { selectAllFirePerimeterBufferLines } from "features/entity-caching/fire-perimeters/selectors"
import { PDFPanel } from "./PDFPanel/PDFPanel"
import { PDFTopBar } from "./PDFTopBar/PDFTopBar"
import { RootState } from "integrations/redux"
import { getInfraredFlightTransformer, getOrthoFlightTransformer } from "tools/transformers/getters"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { customMapController, enabledMapController } from "features/map/control/slice"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { InfraredFlight, OrthoFlight } from "@nova/protobuf"
import { useViewport } from "features/map/GlobalViewState"
import { getAncestors } from "panels/content/side-panel/utils"
import { setActiveTool } from "plugins/Toolbox/tool-registry"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"
import { selectVisibleRasters } from "features/entity-caching/pdf-rasters/selectors"
import { selectVisibleIcons } from "features/entity-caching/icons/selectors"
import { selectOrthoMaskByFlightId } from "features/entity-caching/ortho-masks/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { pdfRasterPath } from "@nova/util-s3"
import { getApiGatewayDomain } from "@nova/fetch-api-gateway"

const maxHeight = 0.8
const maxWidth = 0.65
const id = "buttonTabContainer"

export function PDFMapSnapshot() {
    const dispatch = useAppDispatch()
    const { checkIfBoundingBoxIsOffscreen } = useMapControl()
    const annotationsRef = useRef(null)
    const view = useViewport()

    const projectId = useAppSelector(selectActivatedFireId)

    const pdfLegendSize = useAppSelector(selectPDFLegendSize())
    const orientation = useAppSelector(selectOrientation())
    const legendPosition = useAppSelector(selectLegendPosition())
    const jobId = useAppSelector(selectJobId())
    const infraredFlights = useAppSelector(selectAllVisibleInfraredFlightsMap)
    const orthoFlights = useAppSelector(selectVisibleOrthoFlights)
    const satelliteFlights = useAppSelector(selectVisibleSatelliteFlights)
    const flightPaths = useAppSelector(selectShowLayerByIdMapForFlightPaths)
    const visibleOrthoFlights = useAppSelector(selectVisibleActiveOrthophotos)
    const filename = useAppSelector(selectFilename())
    const notation = useAppSelector(selectNotation())
    const enabled = useAppSelector(selectEnabled())
    const gridData = useAppSelector(selectGridData())
    const mapStylesJSON = useMapStylesJSON()
    const coordinateSystem = useAppSelector(selectCoordinateSystem())
    const size = useAppSelector(selectPageSize())
    const hotspotFlightIds = useAppSelector(selectShowLayerByIdMapForHotspotPoints)
    const hotspotLabelFlightIds = useAppSelector(selectShowLayerByIdMapForHotspotLabels)
    const hotspotPolygonFlightIds = useAppSelector(selectShowLayerByIdMapForHotspotPolygons)
    const infraredScanAreaFlightIds = useAppSelector(selectShowLayerByIdMapForHotspotScanArea)
    const orthoScanAreaFlightIds = useAppSelector(selectShowLayerByIdMapForOrthoScanArea)
    const crs = coordinateSystems[useAppSelector(selectCoordinateSystem())].code
    const hotspotOrthosById = useAppSelector(selectHotspotOrthoVisibilityMap)
    const hotspotOrthos = useMemo(
        () => Object.keys(hotspotOrthosById).filter((key) => hotspotOrthosById[key]),
        [hotspotOrthosById]
    )
    const lines = useAppSelector(selectVisibleLines)
    const texts = useAppSelector(selectVisibleTexts)
    const pdfRasters = useAppSelector(selectVisibleRasters)
    const bufferLines = useAppSelector(selectAllFirePerimeterBufferLines)
    const overlays = useOverlays("project")
    const hideLegend = useAppSelector(selectHideLegend())

    const title = useAppSelector(selectData("title"))
    const subtitle = useAppSelector(selectData("subtitle"))

    const isExtraSmallMode = window.innerWidth < 1300
    const isSmallMode = !isExtraSmallMode && window.innerWidth < 1575

    const fireId = useAppSelector(selectActivatedFireId)!
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!
    const fire = useAppSelector(selectFireById(fireId))!
    const pageHeight = parseFloat(size.split('"')[0])
    const pageWidth = parseFloat(size.split("x")[1].slice(0, -1))

    const aspectRatio =
        orientation === "Landscape" ? pageWidth / pageHeight : pageHeight / pageWidth
    let height = view.height * maxHeight,
        width = height * aspectRatio
    if (width > view.width * maxWidth) {
        // special case aspect ratio has made the width too big, so adjust it smaller based off the width
        width = view.width * maxWidth
        height = width / aspectRatio
    }

    const icons = useAppSelector(selectVisibleIcons)

    const offsetY = (view.height - height) * 0.5,
        offsetX = (view.width - PDFPanelWidth - width) * 0.5
    const mapRatio = hideLegend
        ? 1
        : legendPosition === "right"
          ? (width - (pdfLegendSize * height) / DEFAULT_HEIGHT) / width
          : (height - (pdfLegendSize * width) / DEFAULT_WIDTH) / height

    const name = !filename
        ? `${formatShortDate({ value: new Date() }).replaceAll("/", "_")}_PDF`
        : filename

    const leftLogo = useAppSelector(selectData("leftLogo"))
    const rightLogo = useAppSelector(selectData("rightLogo"))

    // html-to-image doesnt fetch images with credentials, need data url
    useEffect(() => {
        let cancelled = false
        const inline = async (type: "leftLogo" | "rightLogo", src: string) => {
            try {
                const data = await fetchAsDataUrl(src)
                if (!cancelled) dispatch(setData({ type, data }))
            } catch (error) {
                console.error(error)
            }
        }
        if (isCrossOriginLogoSrc(leftLogo)) inline("leftLogo", leftLogo)
        if (isCrossOriginLogoSrc(rightLogo)) inline("rightLogo", rightLogo)
        return () => {
            cancelled = true
        }
    }, [leftLogo, rightLogo, dispatch])

    useEffectUnsafe(() => {
        if (workspaceId) {
            if (leftLogo === leftLogoDefault) {
                dispatch(
                    setData({
                        type: "leftLogo",
                        data: resolveDefaultPdfLogoSrc("left", workspaceId),
                    })
                )
            }

            if (!rightLogo) {
                dispatch(
                    setData({
                        type: "rightLogo",
                        data: resolveDefaultPdfLogoSrc("right", workspaceId),
                    })
                )
            }
        }

        const handleKeydown = (e: KeyboardEvent) => {
            switch (e.key) {
                case "Escape":
                    dispatch(setSnapshotOpen(false))
                    dispatch(setActiveTool(undefined))
                default:
                    return
            }
        }
        dispatch(customMapController({ options: { pitch: 0 } }))
        document.addEventListener("keydown", handleKeydown)

        return () => {
            document.removeEventListener("keydown", handleKeydown)
            dispatch(enabledMapController())
        }
    }, [dispatch])

    const { loading, onSave } = useAsyncCallback(
        {
            onSave: async () => {
                try {
                    const layers: object[] = []
                    if (annotationsRef.current) {
                        const annotationsS3Key = `public/org/${workspaceId}/fire/${fireId}/annotations/${Date.now()}/annotations.png`
                        layers.push({
                            layerType: "annotations",
                            s3Path: annotationsS3Key,
                            fileType: "png",
                            fileName: "annotations.png",
                            layerName: "annotations.png",
                        })
                        const annotationsElement =
                            coordinateSystem === "WGS 84"
                                ? document.getElementById("annotations-ref-full")
                                : annotationsRef.current

                        const maxSize = pageHeight === 36 ? 8000 : pageHeight === 24 ? 6000 : 4000
                        const results = await canvasSize.maxArea({
                            max: maxSize,
                            min: 1000,
                            usePromise: true,
                            step: 1000,
                        })

                        if (results.width !== maxSize) {
                            dispatch(
                                setGlobalNotificationContext({
                                    message:
                                        "High resolution canvases aren't supported in your browser. Output quality may be reduced.",
                                    status: GlobalNotificationStatus.error,
                                    timeoutInMs: 5000,
                                })
                            )
                        }

                        const annotationsBlob = await toBlob(
                            annotationsElement || annotationsRef.current,
                            {
                                canvasWidth: results.width,
                                canvasHeight: (results.height * height) / width,
                            }
                        )
                        if (!annotationsBlob) throw new Error("Could not convert html to blob")
                        await fileManagementApi.putFile(annotationsBlob, {
                            target: annotationsS3Key,
                        })
                    }

                    // flight paths
                    const state = store.getState() as RootState
                    const entityMap = selectAllEntitiesMap(state)
                    for (const flightId of hotspotOrthos) {
                        const bbox = selectHotspotBoundingBoxForFlightId(flightId)(state)
                        if (!checkIfBoundingBoxIsOffscreen(bbox, view)) {
                            const flight = infraredFlights[flightId]
                            const opacity = selectOrthoOpacityById(flightId)(state)
                            const mask = selectOrthoMaskByFlightId(flightId)(state)
                            const maskPolygon =
                                mask?.enabled && mask.polygon?.coordinates.length
                                    ? mask.polygon.coordinates.map(
                                          (c) => [c.longitude, c.latitude] as [number, number]
                                      )
                                    : undefined
                            layers.push({
                                layerType: "tile",
                                layerName: "orthomosaic",
                                fileType: "tile",
                                path: getInfraredFlightTransformer(flightId)?.tileUrl(opacity),
                                maxZoom: flight.job?.maxZoom || 21,
                                maskPolygon,
                            })
                        }
                    }
                    for (const flightId of visibleOrthoFlights) {
                        const bbox = selectOrthoBoundingBoxForFlightId(flightId)(state)
                        if (!checkIfBoundingBoxIsOffscreen(bbox, view)) {
                            const flight = orthoFlights.find((e) => e.id === flightId)
                            if (!flight) continue
                            const opacity = selectOrthoOpacityById(flightId)(state)
                            const mask = selectOrthoMaskByFlightId(flightId)(state)
                            const maskPolygon =
                                mask?.enabled && mask.polygon?.coordinates.length
                                    ? mask.polygon.coordinates.map(
                                          (c) => [c.longitude, c.latitude] as [number, number]
                                      )
                                    : undefined
                            layers.push({
                                layerType: "tile",
                                layerName: "orthomosaic",
                                fileType: "tile",
                                path: getOrthoFlightTransformer(flightId)?.tileUrl(opacity),
                                maxZoom: flight.job?.maxZoom || 21,
                                maskPolygon,
                            })
                        }
                    }
                    const processedSatelliteFlights = satelliteFlights
                        .map((flight) => ({ flight, cogOrtho: selectActiveCogOrtho(flight) }))
                        .filter(
                            (
                                entry
                            ): entry is {
                                flight: typeof entry.flight
                                cogOrtho: NonNullable<typeof entry.cogOrtho>
                            } => !!entry.cogOrtho
                        )
                    for (const { flight, cogOrtho } of processedSatelliteFlights) {
                        const bbox = calculateBoundingBoxCornerPoints(cogOrthoBbox(cogOrtho))
                        if (!checkIfBoundingBoxIsOffscreen(bbox, view)) {
                            const opacity = selectOrthoOpacityById(flight.id)(state)
                            const mask = selectOrthoMaskByFlightId(flight.id)(state)
                            const maskPolygon =
                                mask?.enabled && mask.polygon?.coordinates.length
                                    ? mask.polygon.coordinates.map(
                                          (c) => [c.longitude, c.latitude] as [number, number]
                                      )
                                    : undefined
                            layers.push({
                                layerType: "tile",
                                layerName: "orthomosaic",
                                fileType: "tile",
                                path: getSatelliteFlightTileUrl(
                                    flight,
                                    cogOrtho,
                                    workspaceId,
                                    fireId,
                                    opacity
                                ),
                                maxZoom: cogOrtho.maxZoom || 21,
                                maskPolygon,
                            })
                        }
                    }
                    // Scan areas
                    for (const flight of [...orthoFlights, ...Object.values(infraredFlights)] as (
                        | OrthoFlight
                        | InfraredFlight
                    )[]) {
                        if (
                            flight.visible &&
                            !getAncestors(flight.id, entityMap).find(
                                (ancestor) => !ancestor.visible
                            )
                        ) {
                            const bbox = selectOrthoBoundingBoxForFlightId(flight.id)(state)
                            if (!bbox || !checkIfBoundingBoxIsOffscreen(bbox, view)) {
                                const flightId = flight.id
                                if (
                                    orthoScanAreaFlightIds[flightId] ||
                                    infraredScanAreaFlightIds[flightId]
                                ) {
                                    layers.push({
                                        layerType: "scanArea",
                                        layerName: `${flightId}-scanArea`,
                                        fileType: "kml",
                                        fileName: `${flightId}-scanarea.kml`,
                                        flightId,
                                    })
                                }
                            }
                        }
                    }
                    const processedMergeIds = new Set<string>()
                    for (const flight of Object.values(infraredFlights)) {
                        const flightId = flight.id

                        if (flight.mergeId) {
                            if (processedMergeIds.has(flight.mergeId)) continue
                            processedMergeIds.add(flight.mergeId)
                        }

                        const bbox = flight.mergeId
                            ? selectBoundingBoxForMergedFlight(flight.mergeId)(state)
                            : selectHotspotBoundingBoxForFlightId(flightId)(state)
                        if (bbox && !checkIfBoundingBoxIsOffscreen(bbox, view)) {
                            if (hotspotFlightIds[flight.id]) {
                                // For now, not submitting hotspot layers in hex mode to the PDF job since they can't be renderd.
                                if (flight?.pointType !== HotspotPointTypes.Hex) {
                                    layers.push({
                                        layerType: "hotspots",
                                        layerName: "hotspots", // TODO could use the actual flight name here
                                        fileType: "kml",
                                        fileName: "hotspots.kml",
                                        flightId,
                                        mergeId: flight.mergeId,
                                        labels: hotspotLabelFlightIds[flightId],
                                        heatmap: flight?.pointType === HotspotPointTypes.Heat,
                                        relativeSizeEnabled: !!flight.relativeSizeEnabled,
                                    })
                                }
                            }
                            const flightPathSourceIds = flight.mergeId
                                ? Object.values(infraredFlights)
                                      .filter((f) => f.mergeId === flight.mergeId)
                                      .map((f) => f.id)
                                : [flightId]
                            for (const pathFlightId of flightPathSourceIds) {
                                if (flightPaths[pathFlightId]) {
                                    layers.push({
                                        layerType: "flightpath",
                                        layerName: "flightpath",
                                        fileType: "kml",
                                        fileName: "flightpath.kml",
                                        flightId: pathFlightId,
                                    })
                                }
                            }
                            if (hotspotPolygonFlightIds[flightId]) {
                                layers.push({
                                    layerType: "hotspotPolygon",
                                    layerName: "polygons", // TODO could use the actual flight name here
                                    fileType: "kml",
                                    fileName: "polygons.kml",
                                    flightId,
                                })
                            }
                        }
                    }

                    const [lat, lng] = view.center
                    const bearing = view.bearing

                    const left = offsetX
                    const right = offsetX + (legendPosition === "right" ? width * mapRatio : width)
                    const top = offsetY
                    const bottom =
                        offsetY + (legendPosition === "bottom" ? height * mapRatio : height)

                    const viewport = new WebMercatorViewport(view)
                    const corners = [
                        viewport.unproject([left, top]),
                        viewport.unproject([right, top]),
                        viewport.unproject([right, bottom]),
                        viewport.unproject([left, bottom]),
                    ]
                    const zoom = viewport.zoom

                    const overlayRegionBbox = {
                        west: Math.min(...corners.map((c) => c[0])),
                        east: Math.max(...corners.map((c) => c[0])),
                        south: Math.min(...corners.map((c) => c[1])),
                        north: Math.max(...corners.map((c) => c[1])),
                    }

                    const uploadGeoJsonLayer = async (
                        layerName: string,
                        fileName: string,
                        geoJson: { features: unknown[] },
                        opacity?: number
                    ) => {
                        if (geoJson.features.length === 0) return
                        const s3Path = `public/org/${workspaceId}/fire/${fireId}/pdf-overlays/${Date.now()}/${fileName}`
                        await fileManagementApi.putFile(
                            new Blob([JSON.stringify(geoJson)], { type: "application/geo+json" }),
                            { target: s3Path }
                        )
                        layers.push({
                            layerType: "geojson",
                            layerName,
                            fileType: "geojson",
                            fileName,
                            s3Path,
                            isOverlay: true,
                            ...(opacity !== undefined ? { opacity } : {}),
                        })
                    }

                    if (overlays.heat.enabled) {
                        try {
                            const heat = await getHeatGeoJsonForRegion(overlayRegionBbox, zoom)
                            await uploadGeoJsonLayer(
                                "Heat",
                                "heat.geojson",
                                heat.geoJson,
                                heat.opacity
                            )
                        } catch (err) {
                            console.warn("Failed to include Heat overlay in PDF export", err)
                        }
                    }

                    if (overlays.firePerimeters.enabled) {
                        try {
                            const wildfiresGeoJson =
                                await getWildfirePerimetersGeoJsonForRegion(overlayRegionBbox)
                            await uploadGeoJsonLayer(
                                "Wildfires",
                                "wildfires.geojson",
                                wildfiresGeoJson
                            )
                        } catch (err) {
                            console.warn("Failed to include Wildfires overlay in PDF export", err)
                        }
                    }

                    if (overlays.elevation.enabled) {
                        layers.push({
                            layerType: "tile",
                            layerName: "Elevation",
                            fileType: "tile",
                            path: GIBS_ASTER_GDEM,
                            maxZoom: 12,
                            opacity: 0.5,
                            saturation: -0.2,
                            isOverlay: true,
                        })
                    }

                    if (overlays.civilization.enabled) {
                        layers.push({
                            layerType: "tile",
                            layerName: "Civilization",
                            fileType: "tile",
                            path: GIBS_IMPERVIOUS,
                            maxZoom: 12,
                            opacity: 0.6,
                            isOverlay: true,
                        })
                    }

                    if (overlays.fireDanger.enabled) {
                        for (const source of FIRE_DANGER_WMS_SOURCES) {
                            layers.push({
                                layerType: "wms",
                                layerName: source.layerName,
                                fileType: "wms",
                                wmsBaseUrl: source.baseUrl,
                                wmsLayer: source.wmsLayer,
                                isOverlay: true,
                            })
                        }
                    }

                    if (overlays.geostationary.enabled) {
                        layers.push({
                            layerType: "wms",
                            layerName: GEOSTATIONARY_WMS_SOURCE.layerName,
                            fileType: "wms",
                            wmsBaseUrl: GEOSTATIONARY_WMS_SOURCE.baseUrl,
                            wmsLayer: GEOSTATIONARY_WMS_SOURCE.wmsLayer,
                            isOverlay: true,
                        })
                    }

                    for (const flightId of visibleOrthoFlights) {
                        const bbox = selectOrthoBoundingBoxForFlightId(flightId)(state)
                        if (!bbox || !checkIfBoundingBoxIsOffscreen(bbox, view)) {
                            if (flightPaths[flightId]) {
                                layers.push({
                                    layerType: "flightpath",
                                    layerName: "flightpath", // TODO could use the actual flight name here
                                    fileType: "kml",
                                    fileName: "flightpath.kml",
                                    flightId: flightId,
                                })
                            }
                        }
                    }
                    for (const raster of Object.values(pdfRasters)) {
                        // raster has not been uploaded and cannot be retrieved on the server
                        if (!raster.filename) continue
                        const bbox = calculateBoundingBoxCornerPoints(raster.corners)
                        if (!checkIfBoundingBoxIsOffscreen(bbox, view)) {
                            layers.push(
                                raster.tiled
                                    ? {
                                          layerType: "pdfRaster",
                                          layerName: "pdfRaster",
                                          fileType: "tile",
                                          path: `${getApiGatewayDomain()}/org/${workspaceId}/project/${projectId}/pdf-raster/${encodeURIComponent(raster.filename)}/tile/%7Bz%7D/%7Bx%7D/%7By%7D`,
                                      }
                                    : {
                                          layerType: "raster",
                                          layerName: "pdfRaster",
                                          fileType: "jpg",
                                          fileName: "pdfRaster.jpg",
                                          s3Path: pdfRasterPath(
                                              workspaceId,
                                              fireId,
                                              raster.filename
                                          ),
                                          corners: raster.corners,
                                      }
                            )
                        }
                    }

                    if (
                        lines.filter((line) => line.geometryType === GeometryType.Line).length > 0
                    ) {
                        layers.push({
                            layerType: "line",
                            fileName: "lines.kml",
                            layerName: "line",
                            fileType: "kml",
                        })
                    }

                    if (
                        lines.filter((line) => line.geometryType === GeometryType.Polygon).length >
                        0
                    ) {
                        layers.push({
                            layerType: "polygon",
                            fileName: "polygons.kml",
                            layerName: "line",
                            fileType: "kml",
                        })
                    }

                    if (bufferLines.length > 0) {
                        layers.push({
                            layerType: "buffer",
                            fileName: "buffers.kml",
                            layerName: "buffer",
                            fileType: "kml",
                        })
                    }
                    const jobio = {
                        id: jobId,
                        _embedded: {
                            "instructions.json": {
                                _links: {
                                    self: {
                                        href: "#/_embedded/instructions.json",
                                    },
                                },
                                settings: {
                                    source: "local",
                                    legend: true,
                                    // qr_code: variables.input.showQrCode,
                                    // minimap: variables.input.showInset,
                                    pdf_link: `${window.location.origin}/api/organization/${workspaceId}/job/${jobId}/map.pdf?n=${name}`,
                                    map_source: "osm",
                                },
                                context: {
                                    jobId,
                                    organizationId: workspaceId,
                                    projectId,
                                    title: title || fire.name,
                                    subtitle,
                                    // project,
                                },
                                crs,
                                mapRatio,
                                notation,
                                grid: gridData,
                                gridEnabled: enabled.gridlines,
                                borderTickEnabled: enabled.borderticks,
                                hotspotTableEnabled: enabled.hotspotsTable,
                                useGoogleLinks: enabled.useGoogleLinks,
                                tickPrecision: Math.max(
                                    getDecimals(gridData.x),
                                    getDecimals(gridData.y),
                                    getDecimals(gridData.offsetX),
                                    getDecimals(gridData.offsetY)
                                ),
                                // TODO if no other moving pieces depend on this file, the above could be deleted
                                jobId,
                                organizationId: workspaceId,
                                projectId,
                                // project,
                                filename,
                                page: { size, orientation, legendPosition },
                                viewport: {
                                    corners,
                                    bearing,
                                    zoom,
                                    centerX: lng,
                                    centerY: lat,
                                    width,
                                    height,
                                },
                                layers,
                                layerStyle: JSON.stringify(mapStylesJSON),
                            },
                        },
                    }
                    const jobIOS3Key = `public/org/${workspaceId}/job/${jobId}/job-io.json`
                    await fileManagementApi.putFile(
                        Uint8Array.from(Buffer.from(JSON.stringify(jobio), "utf-8")),
                        { target: jobIOS3Key }
                    )
                    dispatch(
                        createPDFJob({
                            data: {
                                name:
                                    filename ||
                                    `${formatShortDate({ value: new Date() }).replaceAll("/", "_")}_${fire.name}`,
                                id: jobId,
                            },
                        })
                    )
                    dispatch(setSnapshotOpen(false))
                    dispatch(setActiveTool(undefined))
                } catch (err) {
                    console.error(err)
                    dispatch(
                        setGlobalNotificationContext({
                            message: "Failed to create PDF. Please try again.",
                            status: GlobalNotificationStatus.error,
                            timeoutInMs: 5000,
                        })
                    )
                }
            },
        },
        [
            fireId,
            workspaceId,
            view,
            flightPaths,
            offsetX,
            filename,
            offsetY,
            width,
            height,
            lines,
            annotationsRef.current,
            size,
            orientation,
            hotspotFlightIds,
            mapStylesJSON,
            icons,
            coordinateSystem,
            enabled,
            notation,
            gridData,
            hotspotOrthos,
            visibleOrthoFlights,
            hotspotPolygonFlightIds,
            texts,
            title,
            subtitle,
            fire,
            name,
            legendPosition,
            hideLegend,
        ]
    )

    const containerScale = isSmallMode ? 0.8 : isExtraSmallMode ? 0.6 : 1
    useCursorTracking(id, { scale: containerScale })

    return (
        <>
            <PDFTopBar onSave={onSave} loading={loading} />
            <MapSnapshot
                maxHeight={maxHeight}
                maxWidth={maxWidth}
                useViewport={useViewport}
                aspectRatio={aspectRatio}
                rightMargin={PDFPanelWidth}
            />
            <PDFSnapshotContainer
                offsetX={offsetX}
                offsetY={offsetY}
                width={width}
                height={height}
                ref={annotationsRef}
                name={name}
            />
            <PDFPanel offsetX={offsetX} offsetY={offsetY} height={height} width={width} />
        </>
    )
}
